import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { LicenseSettingsCard } from '../components/LicenseSettingsCard';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Layout from '../components/Layout';
import { GuestExperienceCard } from '../components/GuestExperienceCard';
import { LanguageCapCard } from '../components/LanguageCapCard';
import TransitionOverlay from '../components/TransitionOverlay';
import VenueBrandingCard from '../components/VenueBrandingCard';
import { TeamLeadAccessCard } from '../components/TeamLeadAccessCard';
import { WhatsAppConfigCard } from '../components/WhatsAppConfigCard';
import { ResetCacheCard } from '../components/ResetCacheCard';
// PetpoojaConfigCard hidden until direct-ordering is ready for launch (kept in codebase intentionally).
import { TVDisplayCard } from '../components/TVDisplayCard';
import { TVThemeCard } from '../components/TVThemeCard';
import { AdminAILibraryCard } from '../components/AdminAILibraryCard';
import { AiPlaybackDefaultsCard } from '../components/AiPlaybackDefaultsCard';
import { AiScheduleCard } from '../components/AiScheduleCard';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Settings as SettingsIcon, Copy, Download, QrCode, Zap, Loader2, X, Inbox, Sparkles, Tv } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingFlag, setSavingFlag] = useState(null);
  const pollRef = useRef(null);

  const fetchSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/requests/sessions/active`);
      setSession(res.data || null);
      return res.data;
    } catch (err) {
      console.warn('[Settings] fetchSession failed:', err);
      setSession(null);
      return null;
    }
  }, []);

  const fetchPending = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const res = await axios.get(`${API}/requests/sessions/${sid}/requests`);
      const pending = (res.data || []).filter((r) => r.status === 'pending');
      setPendingCount(pending.length);
    } catch (err) {
      console.warn('[Settings] fetchPending failed:', err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const s = await fetchSession();
      if (s?.id) await fetchPending(s.id);
      setLoading(false);
    })();
  }, [fetchSession, fetchPending]);

  useEffect(() => {
    if (!session?.id) return undefined;
    pollRef.current = setInterval(() => fetchPending(session.id), 5000);
    return () => clearInterval(pollRef.current);
  }, [session?.id, fetchPending]);

  const handleCreateSession = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/requests/sessions`);
      setSession(res.data);
      setPendingCount(0);
      toast.success('Session live! Share the QR.');
    } catch (err) {
      console.warn('[Settings] create session failed:', err);
      toast.error('Failed to start session');
    } finally {
      setCreating(false);
    }
  };

  const handleCloseSession = async () => {
    if (!session) return;
    if (!window.confirm("End this session? Guests won't be able to submit requests.")) return;
    try {
      await axios.delete(`${API}/requests/sessions/${session.id}`);
      setSession(null);
      setPendingCount(0);
      toast.success('Session ended');
    } catch (err) {
      console.warn('[Settings] close session failed:', err);
      toast.error('Failed to end session');
    }
  };

  // Session toggle keys that ALSO map to a persistent feature flag on the user
  // record. Toggling these in the live "Request Controls" card should ALSO
  // disable/enable the underlying feature so the change persists past the
  // session — otherwise the host has to re-toggle it every time they go live.
  const SESSION_KEY_TO_FEATURE = {
    requests_enabled: 'song_requests',
    shoutouts_enabled: 'shoutouts',
    reviews_enabled: 'reviews',
  };

  const updateFlag = async (key, value) => {
    if (!session) return;
    setSavingFlag(key);
    try {
      const res = await axios.put(`${API}/requests/sessions/${session.id}/settings`, { [key]: value });
      setSession(res.data);
      // Cascade to the persistent feature flag if this toggle has a mapping.
      const featureKey = SESSION_KEY_TO_FEATURE[key];
      if (featureKey) {
        try {
          await axios.put(`${API}/auth/me/features`, { [featureKey]: value });
        } catch (featErr) {
          // The session-level toggle DID succeed — surface that as the user-
          // visible outcome and only debug-log the feature-flag sync miss.
          console.warn(`[Settings] feature-flag sync for "${featureKey}" failed:`, featErr);
        }
      }
      toast.success('Updated');
    } catch (err) {
      console.warn(`[Settings] updateFlag(${key}) failed:`, err);
      toast.error('Failed to update setting');
    } finally {
      setSavingFlag(null);
    }
  };

  // Permanent venue code — used for the PERMANENT QR (works even when no session is live)
  const [venueCode, setVenueCode] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/venue/my-code`);
        if (!cancelled) setVenueCode(r.data?.venue_code || '');
      } catch (err) {
        console.warn('[Settings] venue code fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copyLink = () => {
    if (!venueCode) {
      toast.error('Venue code still loading');
      return;
    }
    const link = `${FRONTEND_URL}/v/${venueCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Permanent venue link copied!');
  };

  // Hidden high-res QR canvas used only for print export.
  const printQrCanvasRef = useRef(null);

  const downloadQrForPrint = () => {
    if (!venueCode) {
      toast.error('Venue code still loading');
      return;
    }
    const srcCanvas = printQrCanvasRef.current?.querySelector('canvas');
    if (!srcCanvas) {
      toast.error('QR not ready yet, try again');
      return;
    }
    // Table-tent size: 4"×6" at 300 DPI = 1200×1800 px, portrait.
    const W = 1200;
    const H = 1800;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // Top accent bar
    const grad = ctx.createLinearGradient(0, 0, W, 0);
    grad.addColorStop(0, '#FF007F');
    grad.addColorStop(1, '#9D00FF');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, 12);

    // Venue name
    const venueName = (user?.name || 'Scan to Request').toString();
    ctx.fillStyle = '#0a0a0a';
    ctx.textAlign = 'center';
    ctx.font = 'bold 88px "Outfit", "Helvetica Neue", Arial, sans-serif';
    // Truncate super long names
    let title = venueName;
    if (title.length > 24) title = title.slice(0, 22) + '…';
    ctx.fillText(title, W / 2, 180);

    // Subheading
    ctx.fillStyle = '#666';
    ctx.font = '600 44px "Outfit", "Helvetica Neue", Arial, sans-serif';
    ctx.fillText('SCAN TO REQUEST A SONG', W / 2, 260);

    // QR — center, big
    const qrSize = 820;
    const qrX = (W - qrSize) / 2;
    const qrY = 340;
    // White padding around the QR
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40);
    // Draw the QR canvas scaled up
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(srcCanvas, qrX, qrY, qrSize, qrSize);
    ctx.imageSmoothingEnabled = true;

    // Tagline below QR
    ctx.fillStyle = '#111';
    ctx.font = '700 56px "Outfit", "Helvetica Neue", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Or ask the bar', W / 2, qrY + qrSize + 130);
    ctx.font = '400 34px "Outfit", "Helvetica Neue", Arial, sans-serif';
    ctx.fillStyle = '#666';
    ctx.fillText('Point your camera. Verify with OTP.', W / 2, qrY + qrSize + 190);
    ctx.fillText('Pick your track. Vibe on.', W / 2, qrY + qrSize + 240);

    // Bottom bar with URL
    ctx.fillStyle = '#f6f6f6';
    ctx.fillRect(0, H - 120, W, 120);
    ctx.fillStyle = '#111';
    ctx.textAlign = 'center';
    ctx.font = '600 30px "JetBrains Mono", "Courier New", monospace';
    ctx.fillText(`${FRONTEND_URL.replace(/^https?:\/\//, '')}/v/${venueCode}`, W / 2, H - 68);
    ctx.fillStyle = '#999';
    ctx.font = '500 22px "Outfit", Arial, sans-serif';
    ctx.fillText('by MusicBooth', W / 2, H - 32);

    // Trigger download
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error('Export failed — try again');
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = venueName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'venue';
      link.download = `${safeName}-qr-tabletent.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('QR downloaded — ready to print');
    }, 'image/png');
  };

  const copyTvLink = () => {
    if (!venueCode) {
      toast.error('Venue code still loading');
      return;
    }
    const link = `${FRONTEND_URL}/${venueCode}/tv`;
    navigator.clipboard.writeText(link);
    toast.success('TV stream URL copied — paste on every Smart TV!');
  };

  const qrUrl = venueCode ? `${FRONTEND_URL}/v/${venueCode}` : '';
  const tvUrl = venueCode ? `${FRONTEND_URL}/${venueCode}/tv` : '';
  const LayoutWrapper = user?.role === 'superadmin' ? AdminLayout : Layout;

  // ---- Permanent QR card (used both when a session is live AND when offline) ----
  const PermanentQRCard = (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <QrCode className="w-5 h-5 text-[#FF007F]" />
          <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em]" style={{ fontFamily: 'Outfit' }}>
            Venue QR
          </h3>
        </div>
        <span
          className="text-[9px] font-extrabold uppercase tracking-[0.25em] px-2 py-1 rounded bg-[#39FF14]/15 text-[#39FF14]"
          title="This QR works even when no session is live"
        >
          Permanent
        </span>
      </div>
      <div className="flex flex-col sm:flex-row items-start gap-5">
        <div className="bg-white rounded-xl p-4 inline-block flex-shrink-0" data-testid="settings-qr">
          {qrUrl ? (
            <QRCodeSVG value={qrUrl} size={180} level="H" fgColor="#000000" />
          ) : (
            <div className="w-[180px] h-[180px] flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-gray-400 text-[12px] leading-relaxed mb-3">
            Print this QR once. Stick it on every table. It works <span className="text-white font-semibold">forever</span> —
            guests land on a chooser with Menu, Games, Reviews, and (when you&apos;re live) Song Requests.
          </p>
          <p className="text-gray-500 text-[11px] mb-3 break-all font-mono">{qrUrl || 'loading…'}</p>
          <Button
            onClick={copyLink}
            variant="ghost"
            className="border border-white/10 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl text-xs font-semibold tracking-wider w-full sm:w-auto"
            data-testid="settings-copy-link"
          >
            <Copy className="w-3.5 h-3.5 mr-2" /> COPY LINK
          </Button>
          <Button
            onClick={downloadQrForPrint}
            variant="ghost"
            className="ml-0 sm:ml-2 mt-2 sm:mt-0 border border-[#FF007F]/40 text-[#FF007F] hover:text-white hover:bg-[#FF007F]/15 rounded-xl text-xs font-semibold tracking-wider w-full sm:w-auto"
            data-testid="settings-download-qr"
            title="Download a table-tent-sized PNG ready to print"
          >
            <Download className="w-3.5 h-3.5 mr-2" /> DOWNLOAD FOR PRINT
          </Button>
          {/* Hidden high-res QR canvas — sourced by downloadQrForPrint() */}
          <div
            ref={printQrCanvasRef}
            style={{ position: 'absolute', left: '-9999px', top: 0, opacity: 0, pointerEvents: 'none' }}
            aria-hidden="true"
          >
            {qrUrl && <QRCodeCanvas value={qrUrl} size={1024} level="H" fgColor="#000000" bgColor="#ffffff" />}
          </div>
          {session ? (
            <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono text-[#39FF14] px-2 py-1 bg-[#39FF14]/10 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
              LIVE · {session.code}
            </div>
          ) : (
            <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono text-gray-500 px-2 py-1 bg-white/5 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />
              Offline · Menu &amp; Games still work
            </div>
          )}
        </div>
      </div>

      {/* TV Stream URL — same permanent slug, works on every Smart TV browser */}
      <div className="mt-5 pt-5 border-t border-white/5" data-testid="settings-tv-stream-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <Tv className="w-4 h-4 text-[#00FFFF]" />
            <h4 className="text-[12px] font-extrabold text-white uppercase tracking-[0.18em]" style={{ fontFamily: 'Outfit' }}>
              TV Stream URL
            </h4>
            <span
              className="text-[9px] font-extrabold uppercase tracking-[0.25em] px-2 py-0.5 rounded bg-[#00FFFF]/15 text-[#00FFFF]"
              title="Open this URL on every Smart TV browser. Works 24/7 — no setup."
            >
              Permanent
            </span>
          </div>
        </div>
        <p className="text-gray-500 text-[11px] leading-relaxed mb-3">
          Paste this same URL on every Smart TV in the venue. Now-playing, shoutouts, and offers
          all sync live from your DJ Console. No session code, no expiry.
        </p>
        <p className="text-gray-400 text-[11px] mb-3 break-all font-mono bg-black/30 border border-white/5 rounded-lg px-3 py-2">
          {tvUrl || 'loading…'}
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={copyTvLink}
            disabled={!venueCode}
            variant="ghost"
            className="border border-[#00FFFF]/30 text-[#00FFFF] hover:bg-[#00FFFF]/10 rounded-xl text-xs font-bold tracking-wider disabled:opacity-40"
            data-testid="settings-copy-tv-link"
          >
            <Copy className="w-3.5 h-3.5 mr-2" /> COPY TV LINK
          </Button>
          <Button
            onClick={() => venueCode && window.open(`/${venueCode}/tv`, '_blank', 'noopener,noreferrer')}
            disabled={!venueCode}
            variant="ghost"
            className="border border-white/10 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl text-xs font-semibold tracking-wider disabled:opacity-40"
            data-testid="settings-open-tv"
          >
            <Tv className="w-3.5 h-3.5 mr-2" /> OPEN TV VIEW
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <LayoutWrapper>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
        </div>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <div className="p-4 lg:p-8 pb-32 max-w-6xl mx-auto w-full" data-testid="settings-page">
        <div className="flex items-center justify-between mb-6 lg:mb-8 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-6 h-6 lg:w-7 lg:h-7 text-[#FF007F]" />
            <div>
              <h1 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>
                Settings
              </h1>
              <p className="text-gray-400 text-xs lg:text-sm">Session, requests, and integrations.</p>
            </div>
          </div>
          {session ? (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => venueCode && window.open(`/${venueCode}/tv`, '_blank', 'noopener,noreferrer')}
                disabled={!venueCode}
                variant="ghost"
                className="border border-[#00FFFF]/30 text-[#00FFFF] hover:bg-[#00FFFF]/10 rounded-full text-xs font-bold tracking-wider disabled:opacity-40"
                data-testid="settings-launch-tv"
                title="Permanent TV URL — open on any Smart TV browser. Same URL works 24/7."
              >
                <Tv className="w-4 h-4 mr-1.5" /> LAUNCH TV VIEW
              </Button>
              <Button
                onClick={handleCloseSession}
                variant="ghost"
                className="text-[#FF3B3B] hover:bg-[#FF3B3B]/10 rounded-full text-xs font-semibold tracking-wider"
                data-testid="settings-close-session"
              >
                <X className="w-4 h-4 mr-1.5" /> END SESSION
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleCreateSession}
              disabled={creating}
              className="bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full px-5 text-xs font-bold tracking-wider"
              data-testid="settings-start-session"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />}
              GO LIVE
            </Button>
          )}
        </div>

        <div className="mb-4 lg:mb-6"><LicenseSettingsCard /></div>

        {!session ? (
          <div className="space-y-4 lg:space-y-6">
            {/* Permanent QR is ALWAYS visible — even with no live session */}
            {PermanentQRCard}

            <div className="flex flex-col items-center justify-center text-center py-12 px-6 bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl backdrop-blur-xl">
              <div className="w-16 h-16 bg-[#FF007F]/15 rounded-full flex items-center justify-center mb-4">
                <Zap className="w-8 h-8 text-[#FF007F]" />
              </div>
              <h2 className="text-xl lg:text-2xl font-extrabold text-white mb-2 tracking-wider" style={{ fontFamily: 'Outfit' }}>
                NO ACTIVE SESSION
              </h2>
              <p className="text-gray-400 text-sm mb-5 max-w-sm">
                Go live to take song requests, shoutouts, and unlock the LED Light Show. Menu &amp; Games stay open 24/7.
              </p>
              <Button
                onClick={handleCreateSession}
                disabled={creating}
                className="bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full px-8 h-12 font-bold tracking-wider"
                data-testid="settings-start-session-empty"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                GO LIVE
              </Button>
            </div>

            {/* Venue branding (logo + offers) — always editable */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl">
              <VenueBrandingCard />
            </div>

            {/* Guest Experience — pick which tiles appear on the QR landing */}
            <GuestExperienceCard />

            {/* Language Cap — restrict Apple Music search results by language */}
            {user?.client_type !== 'ai' && <LanguageCapCard />}

            {/* TV Display Theme — pick the Quirky / Premium renderer per venue */}
            <TVThemeCard />

            {/* TV Display — music-video toggle + gallery uploads */}
            {/* TV Display Gallery — photos/videos cycled on the TV media slide.
                Available for all venues incl. AI (backend + tv-feed are type-agnostic). */}
            <TVDisplayCard />

            {/* AI Playback Defaults — base genre + energy for AI venues */}
            {user?.client_type === 'ai' && <AiPlaybackDefaultsCard />}

            {/* AI Day-Plan — weekly time-window → genre/energy schedule */}
            {user?.client_type === 'ai' && <AiScheduleCard />}

            {/* AI Music Library — super admin edit tags / delete tracks */}
            <AdminAILibraryCard />

            {/* Team Lead Access — 6-digit PIN for staff to use the /staff mobile view */}
            {user?.client_type !== 'ai' && <TeamLeadAccessCard />}

            {/* App Cache — weekly auto-reset + manual force-reset button */}
            <ResetCacheCard />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {/* Permanent QR — also shown when live (same component, different status pill inside) */}
            {PermanentQRCard}

            {/* Request Controls (renamed to "Live Controls" for AI clients since they can't take song requests) */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Inbox className="w-5 h-5 text-[#39FF14]" />
                  <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em]" style={{ fontFamily: 'Outfit' }}>
                    {user?.client_type === 'ai' ? 'Live Controls' : 'Request Controls'}
                  </h3>
                </div>
                {user?.client_type !== 'ai' && (
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#FF007F]/15 text-[#FF007F]"
                    data-testid="settings-pending-count"
                  >
                    {pendingCount} pending
                  </span>
                )}
              </div>

              <div className="space-y-3">
                {user?.client_type !== 'ai' && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5">
                    <div>
                      <p className="text-white text-sm font-semibold">Requests On</p>
                      <p className="text-gray-500 text-[11px]">When off, the guest page hides the request UI entirely.</p>
                    </div>
                    <Switch
                      checked={!!session.requests_enabled}
                      onCheckedChange={(v) => updateFlag('requests_enabled', v)}
                      disabled={savingFlag === 'requests_enabled'}
                      className="data-[state=checked]:bg-[#39FF14]"
                      data-testid="settings-toggle-requests"
                    />
                  </div>
                )}

                {user?.client_type !== 'ai' && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5">
                    <div>
                      <p className="text-white text-sm font-semibold flex items-center gap-2">
                        Auto Accept
                        <Sparkles className="w-3.5 h-3.5 text-[#FF007F]" />
                      </p>
                      <p className="text-gray-500 text-[11px]">Incoming requests skip approval and go straight to next-up in the queue.</p>
                    </div>
                    <Switch
                      checked={!!session.auto_accept}
                      onCheckedChange={(v) => updateFlag('auto_accept', v)}
                      disabled={savingFlag === 'auto_accept'}
                      className="data-[state=checked]:bg-[#FF007F]"
                      data-testid="settings-toggle-auto-accept"
                    />
                  </div>
                )}

                {user?.features?.shoutouts !== false && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5">
                    <div>
                      <p className="text-white text-sm font-semibold">Shoutouts On</p>
                      <p className="text-gray-500 text-[11px]">When off, guests cannot send shoutout messages.</p>
                    </div>
                    <Switch
                      checked={session.shoutouts_enabled !== false}
                      onCheckedChange={(v) => updateFlag('shoutouts_enabled', v)}
                      disabled={savingFlag === 'shoutouts_enabled'}
                      className="data-[state=checked]:bg-[#39FF14]"
                      data-testid="settings-toggle-shoutouts"
                    />
                  </div>
                )}

                {user?.features?.reviews !== false && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5">
                    <div>
                      <p className="text-white text-sm font-semibold">Reviews On</p>
                      <p className="text-gray-500 text-[11px]">When off, guests won&apos;t see the &quot;Leave a Review&quot; tile.</p>
                    </div>
                    <Switch
                      checked={session.reviews_enabled !== false}
                      onCheckedChange={(v) => updateFlag('reviews_enabled', v)}
                      disabled={savingFlag === 'reviews_enabled'}
                      className="data-[state=checked]:bg-[#FFD700]"
                      data-testid="settings-toggle-reviews"
                    />
                  </div>
                )}

                {user?.features?.games && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5">
                    <div>
                      <p className="text-white text-sm font-semibold">Games On</p>
                      <p className="text-gray-500 text-[11px]">When off, guests won&apos;t see the &quot;Play Games&quot; tile.</p>
                    </div>
                    <Switch
                      checked={session.games_enabled !== false}
                      onCheckedChange={(v) => updateFlag('games_enabled', v)}
                      disabled={savingFlag === 'games_enabled'}
                      className="data-[state=checked]:bg-[#FF8C00]"
                      data-testid="settings-toggle-games"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Language Cap — restrict Apple Music search results by language */}
            {user?.client_type !== 'ai' && <LanguageCapCard />}

            {/* TV Display Theme — pick the Quirky / Premium renderer per venue */}
            <TVThemeCard />

            {/* TV Display — music-video toggle + gallery uploads */}
            {/* TV Display Gallery — photos/videos cycled on the TV media slide.
                Available for all venues incl. AI (backend + tv-feed are type-agnostic). */}
            <TVDisplayCard />

            {/* AI Playback Defaults — base genre + energy for AI venues (shown
                in BOTH layouts so it's reachable whether or not a session is live) */}
            {user?.client_type === 'ai' && <AiPlaybackDefaultsCard />}

            {/* AI Day-Plan — weekly time-window → genre/energy schedule */}
            {user?.client_type === 'ai' && <AiScheduleCard />}

            {/* AI Music Mode — shown only for venues on the in-house AI library */}
            {/* AI mode picker + mixer lives on the AI Booth home page now
                (Feb 2026). Settings no longer duplicates it. */}

            {/* AI Music Library — super admin only (edit tags / delete tracks) */}
            {user?.role === 'superadmin' && <AdminAILibraryCard />}

            {/* Venue branding (logo + offers) */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl">
              <VenueBrandingCard />
            </div>

            {/* Team Lead Access — 6-digit PIN for staff to use the /staff mobile view */}
            {user?.client_type !== 'ai' && <TeamLeadAccessCard />}

            {/* WhatsApp — guest OTP gate + staff notification numbers */}
            <WhatsAppConfigCard />

            {/* App Cache — weekly auto-reset + manual force-reset button */}
            <ResetCacheCard />
          </div>
        )}
      </div>
      <TransitionOverlay qrUrl={qrUrl} />
    </LayoutWrapper>
  );
};

export default SettingsPage;
